import React, { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@klear/ui/components/dialog'
import { Button } from '@klear/ui/components/button'
import { BasisPointsInput } from '@klear/ui/components/basis-points-input'
import { BasisPoints } from '@klear/basis-points'
import { Checkbox } from '@klear/ui/components/checkbox'
import { Label } from '@klear/ui/components/label'
import { useFunderRateSettingsMutation } from '@klear/ui/hooks/use-funder-pricing'
import {
  funderRateSettingsFormToUpdateWire,
  funderReadToRateSettingsForm,
  type FunderRateSettingsForm
} from '@klear/funder/wire.client'
import type { RouterOutputs } from '@klear/ui/lib/trpc'

type FunderRow = RouterOutputs['funder']['list'][number]

interface RateAllocationDialogProps {
  isOpen: boolean
  onClose: () => void
  funder: FunderRow | null
}

const RateAllocationForm: React.FC<{ funder: FunderRow; onClose: () => void }> = ({ funder, onClose }) => {
  const { updateFunderRateSettings, isUpdating } = useFunderRateSettingsMutation()
  const [form, setForm] = useState<FunderRateSettingsForm>(() =>
    funderReadToRateSettingsForm({
      funderRate: funder.funderRate.value,
      supportsIG: funder.supportsIG,
      supportsInsured: funder.supportsInsured
    })
  )

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    try {
      await updateFunderRateSettings(funderRateSettingsFormToUpdateWire(form, funder.id))
      onClose()
    } catch (error) {
      console.error('Failed to save funder rate settings:', error)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <p className="text-sm font-medium">{funder.organizationName || `Funder ${funder.id}`}</p>
        <p className="text-xs text-muted-foreground">Applies to all IG/Insured pricing this SPV funds.</p>
      </div>

      <BasisPointsInput
        label="SPV Rate"
        value={new BasisPoints(form.funderRate)}
        onChange={(value) => setForm((prev) => ({ ...prev, funderRate: value?.value ?? 0 }))}
        helperText="Portion of the annualized rate allocated to the SPV (e.g. 2.50% = 250 bps)"
        disabled={isUpdating}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="flex items-center gap-2">
          <Checkbox
            id="supportsIG"
            checked={form.supportsIG}
            onCheckedChange={(checked) => setForm((prev) => ({ ...prev, supportsIG: checked === true }))}
            disabled={isUpdating}
          />
          <Label htmlFor="supportsIG">Supports IG (Allow investment-grade pricing)</Label>
        </div>
        <div className="flex items-center gap-2">
          <Checkbox
            id="supportsInsured"
            checked={form.supportsInsured}
            onCheckedChange={(checked) => setForm((prev) => ({ ...prev, supportsInsured: checked === true }))}
            disabled={isUpdating}
          />
          <Label htmlFor="supportsInsured">Supports Insured (Allow Allianz/Insured pricing)</Label>
        </div>
      </div>

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose} disabled={isUpdating}>
          Cancel
        </Button>
        <Button type="submit" disabled={isUpdating}>
          {isUpdating ? 'Saving...' : 'Update'}
        </Button>
      </DialogFooter>
    </form>
  )
}

const RateAllocationDialog: React.FC<RateAllocationDialogProps> = ({ isOpen, onClose, funder }) => {
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Edit Funder Rate</DialogTitle>
          <DialogDescription>
            Update the single rate allocation and which rating scales this SPV supports for invoice offers.
          </DialogDescription>
        </DialogHeader>

        {funder && <RateAllocationForm key={funder.id} funder={funder} onClose={onClose} />}
      </DialogContent>
    </Dialog>
  )
}

export default RateAllocationDialog
